import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
  Stack,
  Typography,
  Autocomplete,
  Checkbox,
  FormControlLabel
} from '@mui/material';

// ─── Create Blend Dialog ─────────────────────────────────────────────
export function CreateBlendDialog({ open, onClose, newBlend, setNewBlend, onSubmit }) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Create New Blend</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 2 }}>
          <TextField
            label="Code"
            value={newBlend.code}
            onChange={(e) => setNewBlend(prev => ({ ...prev, code: e.target.value }))}
          />
          <TextField
            label="Description"
            value={newBlend.description}
            onChange={(e) => setNewBlend(prev => ({ ...prev, description: e.target.value }))}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSubmit} variant="contained">Create</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Edit Blend Dialog ───────────────────────────────────────────────
export function EditBlendDialog({ open, onClose, blendDetails, setBlendDetails, onSubmit }) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Edit Blend</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 2 }}>
          <TextField
            label="Code"
            value={blendDetails?.code || ''}
            onChange={(e) => setBlendDetails(prev => ({ ...prev, code: e.target.value }))}
          />
          <TextField
            label="Description"
            value={blendDetails?.description || ''}
            onChange={(e) => setBlendDetails(prev => ({ ...prev, description: e.target.value }))}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSubmit} variant="contained">Update</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Create Version Dialog ───────────────────────────────────────────
export function CreateVersionDialog({
  open,
  onClose,
  newVersion,
  setNewVersion,
  createFromVersion,
  setCreateFromVersion,
  selectedBaseVersion,
  setSelectedBaseVersion,
  versions,
  itemsByVersion,
  onSubmit
}) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Create New Version</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 2 }}>
          <TextField
            label="Version Name"
            value={newVersion.versionName}
            onChange={(e) => setNewVersion(prev => ({ ...prev, versionName: e.target.value }))}
          />
          <TextField
            label="Description"
            value={newVersion.description}
            onChange={(e) => setNewVersion(prev => ({ ...prev, description: e.target.value }))}
          />
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={newVersion.status}
              label="Status"
              onChange={(e) => setNewVersion(prev => ({ ...prev, status: e.target.value }))}
            >
              <MenuItem value="Actual">Actual</MenuItem>
              <MenuItem value="Sample">Sample</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Cut Width"
            value={newVersion.cutWidth || ''}
            onChange={(e) => setNewVersion(prev => ({ ...prev, cutWidth: e.target.value }))}
          />
          <TextField
            label="Moisture"
            value={newVersion.moisture || ''}
            onChange={(e) => setNewVersion(prev => ({ ...prev, moisture: e.target.value }))}
          />
          <TextField
            label="Temperature"
            value={newVersion.temperature || ''}
            onChange={(e) => setNewVersion(prev => ({ ...prev, temperature: e.target.value }))}
          />
          <TextField
            label="Nicotine Tolerance (%)"
            type="number"
            value={newVersion.nicotineTolerancePct || ''}
            onChange={(e) => setNewVersion(prev => ({ ...prev, nicotineTolerancePct: e.target.value }))}
            inputProps={{ step: 0.01, min: 0 }}
          />
          <TextField
            label="Sugar Tolerance (%)"
            type="number"
            value={newVersion.sugarTolerancePct || ''}
            onChange={(e) => setNewVersion(prev => ({ ...prev, sugarTolerancePct: e.target.value }))}
            inputProps={{ step: 0.01, min: 0 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={createFromVersion}
                onChange={(e) => {
                  setCreateFromVersion(e.target.checked);
                  if (!e.target.checked) setSelectedBaseVersion(null);
                }}
              />
            }
            label="Create from existing version"
          />

          {createFromVersion && (
            <Autocomplete
              options={versions}
              value={selectedBaseVersion}
              onChange={(event, newValue) => setSelectedBaseVersion(newValue)}
              getOptionLabel={(option) => `${option.versionName} (${option.status || 'Actual'}) - ${new Date(option.timestamp).toLocaleDateString()}`}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Stack>
                    <Typography variant="body2" fontWeight="bold">
                      {option.versionName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Status: {option.status || 'Actual'} | Created: {new Date(option.timestamp).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Items: {itemsByVersion[option.id]?.length || 'Not loaded'}
                    </Typography>
                  </Stack>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Base Version"
                  placeholder="Choose a version to copy items from..."
                  helperText="Select a version to copy its items to the new version"
                />
              )}
              disabled={!createFromVersion}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={createFromVersion && !selectedBaseVersion}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Edit Version Dialog ─────────────────────────────────────────────
export function EditVersionDialog({ open, onClose, versionForEdit, setVersionForEdit, onSubmit }) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Edit Version</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 2 }}>
          <TextField
            label="Version Name"
            value={versionForEdit?.versionName || ''}
            onChange={(e) => setVersionForEdit(prev => ({ ...prev, versionName: e.target.value }))}
          />
          <TextField
            label="Description"
            value={versionForEdit?.description || ''}
            onChange={(e) => setVersionForEdit(prev => ({ ...prev, description: e.target.value }))}
          />
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={versionForEdit?.status || 'Actual'}
              label="Status"
              onChange={(e) => setVersionForEdit(prev => ({ ...prev, status: e.target.value }))}
            >
              <MenuItem value="Actual">Actual</MenuItem>
              <MenuItem value="Sample">Sample</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Cut Width"
            value={versionForEdit?.cutWidth || ''}
            onChange={(e) => setVersionForEdit(prev => ({ ...prev, cutWidth: e.target.value }))}
          />
          <TextField
            label="Moisture"
            value={versionForEdit?.moisture || ''}
            onChange={(e) => setVersionForEdit(prev => ({ ...prev, moisture: e.target.value }))}
          />
          <TextField
            label="Temperature"
            value={versionForEdit?.temperature || ''}
            onChange={(e) => setVersionForEdit(prev => ({ ...prev, temperature: e.target.value }))}
          />
          <TextField
            label="Nicotine Tolerance (%)"
            type="number"
            value={versionForEdit?.nicotineTolerancePct || ''}
            onChange={(e) => setVersionForEdit(prev => ({ ...prev, nicotineTolerancePct: e.target.value }))}
            inputProps={{ step: 0.01, min: 0 }}
          />
          <TextField
            label="Sugar Tolerance (%)"
            type="number"
            value={versionForEdit?.sugarTolerancePct || ''}
            onChange={(e) => setVersionForEdit(prev => ({ ...prev, sugarTolerancePct: e.target.value }))}
            inputProps={{ step: 0.01, min: 0 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSubmit} variant="contained">Update</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Duplicate Version Dialog ────────────────────────────────────────
export function DuplicateVersionDialog({
  open,
  onClose,
  versionToDuplicate,
  duplicateVersionName,
  setDuplicateVersionName,
  itemsByVersion,
  onSubmit
}) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Duplicate Version</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This will create a copy of &quot;{versionToDuplicate?.versionName}&quot; including all its items.
        </DialogContentText>
        <Stack spacing={2} sx={{ pt: 2 }}>
          <TextField
            label="New Version Name"
            value={duplicateVersionName}
            onChange={(e) => setDuplicateVersionName(e.target.value)}
            fullWidth
            autoFocus
            helperText="Enter a name for the duplicated version"
          />
          {versionToDuplicate && (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Original Version: {versionToDuplicate.versionName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Status: {versionToDuplicate.status || 'Actual'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Items to copy: {itemsByVersion[versionToDuplicate.id]?.length || 'Unknown (will be loaded)'}
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={!duplicateVersionName.trim()}
        >
          Duplicate
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Edit Item Dialog ────────────────────────────────────────────────
const ITEM_FIELDS = ['description', 'quantity', 'unit'];

export function EditItemDialog({ open, onClose, itemForEdit, setItemForEdit, onSubmit }) {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Edit Item</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 2 }}>
          {ITEM_FIELDS.map(field => (
            <TextField
              key={field}
              label={field.charAt(0).toUpperCase() + field.slice(1)}
              value={itemForEdit?.[field] || ''}
              onChange={(e) => setItemForEdit(prev => ({ ...prev, [field]: e.target.value }))}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSubmit} variant="contained">Update</Button>
      </DialogActions>
    </Dialog>
  );
}
